import type { TonoDeEtiqueta } from '@/components/ui/etiqueta';
import { formatoPrecio } from '@/lib/utils/formato-precio';
import type { Action, PricingRule, RuleScope, TipoDeRegla } from '../types/pricing-rules.types';

/**
 * Las reglas dichas en palabras (spec 208 §1, maqueta S1–S3). Ninguna función
 * de acá devuelve un nombre interno (`FORCE_MIN_MARGIN`, `scope`, `priority`).
 */

const MODOS_REDONDEO: Record<string, string> = {
  NEAREST_99: 'Terminar en ,99',
  NEAREST_90: 'Terminar en ,90',
  NEAREST_00: 'Terminar en entero',
  NEAREST_50: 'Múltiplo de 50',
};

// En español: coma decimal (spec 208 «ganás 13,3 %»), y punto de miles en los
// montos fijos. Un entero (20) no arrastra ceros de más («20 %», no «20,00 %»).
const FORMATO_NUMERO = new Intl.NumberFormat('es-BO', { maximumFractionDigits: 2 });
/** 12.5 → «12,5»; 3250 → «3.250». Lo usan todas las pantallas de reglas. */
export const numero = (n: number): string => FORMATO_NUMERO.format(n);

type Movimiento = Extract<Action, { type: 'MAX_CHANGE_PERCENT' }>;
const esMovimiento = (a: Action): a is Movimiento => a.type === 'MAX_CHANGE_PERCENT';

/**
 * Los topes de una regla de movimiento, por sentido (decisión 8). Una acción sin
 * `direction` (o BOTH) cuenta para los dos. `null` = la regla no es de movimiento.
 */
export function topesDeMovimiento(
  actions: Action[]
): { subida: number | null; bajada: number | null } | null {
  if (actions.length === 0 || !actions.every(esMovimiento)) return null;
  let subida: number | null = null;
  let bajada: number | null = null;
  for (const a of actions as Movimiento[]) {
    const d = a.direction ?? 'BOTH';
    if (d === 'BOTH' || d === 'UP')
      subida = subida == null ? a.percent : Math.min(subida, a.percent);
    if (d === 'BOTH' || d === 'DOWN')
      bajada = bajada == null ? a.percent : Math.min(bajada, a.percent);
  }
  return { subida, bajada };
}

/** La única regla de dos acciones que se puede crear: subida y bajada por separado. */
export function esMovimientoPorSentido(actions: Action[]): boolean {
  if (actions.length !== 2 || !actions.every(esMovimiento)) return false;
  const dirs = (actions as Movimiento[]).map((a) => a.direction ?? 'BOTH').sort();
  return dirs[0] === 'DOWN' && dirs[1] === 'UP';
}

/**
 * El tipo de una regla que se puede crear hoy. `null` = una regla vieja (monto
 * fijo, margen máximo, acción retirada, o varias acciones juntas).
 */
export function tipoDeRegla(actions: Action[]): TipoDeRegla | null {
  if (actions.length === 0) return null;
  if (actions.length > 1) return esMovimientoPorSentido(actions) ? 'MOVIMIENTO' : null;
  switch (actions[0].type) {
    case 'FORCE_MIN_MARGIN':
      return 'MARGEN_MINIMO';
    case 'MAX_CHANGE_PERCENT':
      return 'MOVIMIENTO';
    case 'MAX_ABOVE_COMPETITOR_MIN':
      return 'SOBRE_EL_MAS_BARATO';
    // Lote B2 (D6, D7): los tres tipos nuevos.
    case 'MAX_BELOW_COMPETITOR_MIN':
      return 'BAJO_EL_MAS_BARATO';
    case 'FORCE_FLOOR':
      return 'PISO_FIJO';
    case 'FORCE_MAX_PRICE':
      return 'TECHO_FIJO';
    case 'ROUND_PRICE':
      return 'REDONDEO';
    default:
      return null;
  }
}

export function describirRegla(a: Action): string {
  switch (a.type) {
    case 'FORCE_MIN_MARGIN':
      return `No ganar menos de ${numero(a.percent)} %`;
    case 'MAX_CHANGE_PERCENT': {
      const d = a.direction ?? 'BOTH';
      if (a.percent === 0 && d !== 'BOTH') return d === 'UP' ? 'Nunca subir' : 'Nunca bajar';
      const verbo = d === 'UP' ? 'subir' : d === 'DOWN' ? 'bajar' : 'mover';
      return `No ${verbo} más de ${numero(a.percent)} % por vez`;
    }
    case 'MAX_ABOVE_COMPETITOR_MIN':
      return `No quedar más de ${numero(a.percent)} % arriba del más barato`;
    case 'MAX_BELOW_COMPETITOR_MIN':
      return `No quedar más de ${numero(a.percent)} % debajo del más barato`;
    case 'ROUND_PRICE':
      return a.mode === 'CUSTOM' ? `Múltiplo de ${a.customValue ?? '—'}` : MODOS_REDONDEO[a.mode];
    case 'FORCE_MAX_MARGIN':
      return `No ganar más de ${numero(a.percent)} %`;
    // Lote B2 (D6): los límites en Bs, con el precio con sus dos decimales.
    case 'FORCE_FLOOR':
      return `No vender por debajo de Bs ${formatoPrecio(a.value)}`;
    case 'FORCE_MAX_PRICE':
      return `No vender por encima de Bs ${formatoPrecio(a.value)}`;
    default:
      return 'Una acción que se retiró';
  }
}

/** Todas las acciones de la regla, no sólo la primera. */
export function describirReglaCompleta(actions: Action[]): string {
  if (actions.length === 0) return 'Sin ningún límite';
  if (esMovimientoPorSentido(actions)) {
    const t = topesDeMovimiento(actions)!;
    // Con una UP y una DOWN (esMovimientoPorSentido), los dos lados quedan seteados: nunca null acá.
    const subir = t.subida === 0 ? 'nunca subir' : `no subir más de ${numero(t.subida!)} %`;
    const bajar = t.bajada === 0 ? 'nunca bajar' : `no bajar más de ${numero(t.bajada!)} %`;
    const porVez = t.subida === 0 || t.bajada === 0 ? '' : ' por vez';
    const frase = `${subir} · ${bajar}${porVez}`;
    return frase.charAt(0).toUpperCase() + frase.slice(1);
  }
  return actions.map(describirRegla).join(' · ');
}

export function describirAlcance(s: RuleScope): string {
  const partes: string[] = [];
  if (s.categories?.length) partes.push(s.categories.join(', '));
  if (s.brands?.length) partes.push(s.brands.join(', '));
  if (s.skus?.length)
    partes.push(s.skus.length === 1 ? '1 producto' : `${s.skus.length} productos`);
  const base = partes.length ? partes.join(' · ') : 'Todo el catálogo';
  const n = s.excludedSkus?.length ?? 0;
  return n ? `${base}, menos ${n === 1 ? '1 producto' : `${n} productos`}` : base;
}

/**
 * El alcance dentro de una frase («… alcanza a todo el catálogo», «… alcanza a
 * Sony»): sólo «Todo el catálogo» va en minúscula. Pasar todo a minúsculas
 * escribía «sony» o «televisores · lg» (revisión final, B6).
 */
export function alcanceEnFrase(s: RuleScope): string {
  const t = describirAlcance(s);
  return t.startsWith('Todo el catálogo') ? `t${t.slice(1)}` : t;
}

export const esTodoElCatalogo = (s: RuleScope | null | undefined): boolean =>
  !s?.categories?.length && !s?.brands?.length && !s?.skus?.length;

/** Lote B2 (D6): «No vender por debajo / por encima de Bs X». */
export const esLimiteEnBs = (t: TipoDeRegla | null | undefined): boolean =>
  t === 'PISO_FIJO' || t === 'TECHO_FIJO';

/** Lote B2 (D6): un solo producto, sin categorías, marcas ni excepciones (lo que pide el backend). */
export const esDeUnProducto = (s: RuleScope | null | undefined): boolean =>
  (s?.skus?.length ?? 0) === 1 &&
  !s?.categories?.length &&
  !s?.brands?.length &&
  !s?.excludedSkus?.length;

type ReglaParaEstado = Pick<
  PricingRule,
  'isActive' | 'conditions' | 'actions' | 'validFrom' | 'validUntil'
> & {
  /** Lote B2 (D6): para decir que un límite en Bs para varios productos ya no se crea así. */
  scope?: RuleScope | null;
};

/** Lote B2 (D6): un límite en Bs guardado para varios productos (una regla de antes). */
const enBsParaVarios = (r: ReglaParaEstado, tipo: TipoDeRegla | null): boolean =>
  esLimiteEnBs(tipo) && r.scope !== undefined && !esDeUnProducto(r.scope);

export type PorQueNoCorre = 'APAGADA' | 'VENCIDA' | 'POR_EMPEZAR' | 'CONDICIONES' | 'RETIRADA';

export type EstadoDeRegla =
  | { corre: true; tipo: TipoDeRegla | null }
  | { corre: false; tipo: TipoDeRegla | null; porque: PorQueNoCorre };

const RETIRADAS = new Set(['APPLY_DISCOUNT', 'ADJUST_PERCENT', 'MATCH_COMPETITOR_MIN']);

/**
 * Si la regla corre hoy y, si no, por qué. Es lo que hace el motor del 4A: las
 * condiciones la excluyen; un monto fijo o un margen máximo SÍ corren. La
 * vigencia es la de la regla: la migración del 4B-datos (T1) le pasó la del grupo.
 */
export function estadoDeRegla(r: ReglaParaEstado, ahora: Date = new Date()): EstadoDeRegla {
  const tipo = tipoDeRegla(r.actions);
  if ((r.conditions?.all?.length ?? 0) > 0) return { corre: false, tipo, porque: 'CONDICIONES' };
  if (r.actions.length > 0 && r.actions.every((a) => RETIRADAS.has(a.type)))
    return { corre: false, tipo, porque: 'RETIRADA' };
  if (!r.isActive) return { corre: false, tipo, porque: 'APAGADA' };
  if (r.validUntil && new Date(r.validUntil) < ahora)
    return { corre: false, tipo, porque: 'VENCIDA' };
  if (r.validFrom && new Date(r.validFrom) > ahora)
    return { corre: false, tipo, porque: 'POR_EMPEZAR' };
  return { corre: true, tipo };
}

// `validFrom`/`validUntil` son días de calendario, guardados como
// `YYYY-MM-DDT00:00:00Z`: leerlos sin `timeZone: 'UTC'` los corre un día para
// atrás en Bolivia (UTC−4) — «vence el 1-sept» se leía «vence el 31-ago».
// Ronda final (B9): se queda con `toLocaleDateString` a propósito: `formatoFecha`
// no acepta zona horaria y acá la UTC es el dato.
const OPCIONES_DE_FECHA = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
} as const;

const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-BO', OPCIONES_DE_FECHA); // pulido: permitido — la zona UTC es el dato (días de calendario, B9).
/** El día de calendario de una regla («10 oct 2026»), sin correrlo por la zona. */
export const fechaDeCalendario = fecha;
/**
 * La línea que va debajo de una regla que no corre, o que ya no se puede crear
 * así. `null` = nada que decir (una apagada se ve en el interruptor).
 */
export function avisoDeRegla(r: ReglaParaEstado, ahora: Date = new Date()): string | null {
  const e = estadoDeRegla(r, ahora);
  if (enBsParaVarios(r, e.tipo) && (e.corre || e.porque === 'APAGADA'))
    return `${e.corre ? 'Sigue corriendo, pero un' : 'Un'} límite en Bs ya no se crea para varios productos: al editarla elegís uno solo.`;
  if (e.corre) {
    return e.tipo == null
      ? 'Sigue corriendo, pero ya no se crean así: al editarla elegís uno de los límites de hoy, en %.'
      : null;
  }
  switch (e.porque) {
    case 'CONDICIONES':
      return 'Se creó con requisitos extra (días de stock, ventas, margen proyectado) que ya no existen: no corre hasta editarla. Al editarla va a alcanzar a todos los productos de su alcance.';
    case 'RETIRADA':
      return 'Tiene una acción que se retiró: no hace nada hasta editarla.';
    case 'VENCIDA':
      return `No corre: venció el ${fecha(r.validUntil!)}.`;
    case 'POR_EMPEZAR':
      return `Empieza a correr el ${fecha(r.validFrom!)}.`;
    default:
      return e.tipo == null
        ? 'Ya no se crean así: al editarla elegís uno de los límites de hoy, en %.'
        : null;
  }
}

/**
 * La etiqueta corta de cada fila de la lista (pulido, maqueta Reglas). El
 * porqué largo de las que no corren va una sola vez arriba (`lasQueNoCorren`):
 * repetido idéntico en ocho filas no se leía. `null` = nada que marcar (una
 * apagada de hoy se ve en el interruptor).
 */
export function etiquetaDeRegla(
  r: ReglaParaEstado,
  ahora: Date = new Date()
): { texto: string; tono: TonoDeEtiqueta } | null {
  const e = estadoDeRegla(r, ahora);
  if (enBsParaVarios(r, e.tipo) && (e.corre || e.porque === 'APAGADA'))
    return {
      texto: `${e.corre ? 'sigue corriendo · ' : ''}al editarla elegís un solo producto`,
      tono: 'neutro',
    };
  if (e.corre)
    return e.tipo == null
      ? { texto: 'sigue corriendo · al editarla pasa a un límite en %', tono: 'neutro' }
      : null;
  switch (e.porque) {
    case 'CONDICIONES':
    case 'RETIRADA':
      return { texto: 'no corre hasta editarla', tono: 'alerta' };
    case 'VENCIDA':
      return { texto: `no corre: venció el ${fecha(r.validUntil!)}`, tono: 'alerta' };
    case 'POR_EMPEZAR':
      return { texto: `empieza a correr el ${fecha(r.validFrom!)}`, tono: 'info' };
    default:
      return e.tipo == null ? { texto: 'al editarla pasa a un límite en %', tono: 'neutro' } : null;
  }
}

/**
 * El aviso de arriba de la lista: cuántas no corren hasta que alguien las edite
 * y por qué, dicho una vez. `null` si no hay ninguna.
 */
export function lasQueNoCorren(
  reglas: ReglaParaEstado[],
  ahora: Date = new Date()
): { titulo: string; texto: string } | null {
  const porque = reglas.map((r) => estadoDeRegla(r, ahora)).map((e) => (e.corre ? null : e.porque));
  const conRequisitos = porque.filter((p) => p === 'CONDICIONES').length;
  const retiradas = porque.filter((p) => p === 'RETIRADA').length;
  const total = conRequisitos + retiradas;
  if (total === 0) return null;
  const partes: string[] = [];
  if (conRequisitos === 1)
    partes.push(
      `${retiradas > 0 ? '1 se creó' : 'Se creó'} con requisitos extra (días de stock, ventas, margen proyectado) que ya no existen. Al editarla va a alcanzar a todos los productos de su alcance.`
    );
  else if (conRequisitos > 1)
    partes.push(
      `${retiradas > 0 ? `${numero(conRequisitos)} se crearon` : 'Se crearon'} con requisitos extra (días de stock, ventas, margen proyectado) que ya no existen. Al editarlas van a alcanzar a todos los productos de su alcance.`
    );
  if (retiradas === 1)
    partes.push(
      `${conRequisitos > 0 ? '1 tiene' : 'Tiene'} una acción que se retiró: no hace nada hasta editarla.`
    );
  else if (retiradas > 1)
    partes.push(
      `${numero(retiradas)} tienen una acción que se retiró: no hacen nada hasta editarlas.`
    );
  return {
    titulo:
      total === 1
        ? '1 regla no corre hasta que la edites'
        : `${numero(total)} reglas no corren hasta que las edites`,
    texto: partes.join(' '),
  };
}

/**
 * Spec 294 (D-R4-4): los campos de un cambio sin aplicar, en palabras («el
 * límite, a quién alcanza y el nombre»), en el orden que llegan. Un campo que no
 * conoce no se nombra: nunca el nombre interno. Sin ninguno conocido, «algo».
 */
const CAMPOS_EN_PALABRAS = new Map<string, string>(
  Object.entries({
    name: 'el nombre',
    description: 'la descripción',
    isActive: 'el encendido',
    priority: 'el orden',
    onStop: 'qué hace si no hay precio posible',
    scope: 'a quién alcanza',
    conditions: 'los requisitos',
    actions: 'el límite',
    validFrom: 'la fecha de inicio',
    validUntil: 'la fecha de fin',
    notes: 'las notas',
    tags: 'las etiquetas',
  })
);

export function camposEnPalabras(campos: string[]): string {
  const dichos = campos.map((c) => CAMPOS_EN_PALABRAS.get(c)).filter((x): x is string => !!x);
  if (dichos.length === 0) return 'algo';
  if (dichos.length === 1) return dichos[0];
  return `${dichos.slice(0, -1).join(', ')} y ${dichos[dichos.length - 1]}`;
}
